/**
 * theme/hollow.ts — the CAVE cache theme: a cellular-automata carve, realised as
 * extruded rock prisms under a ceiling shell.
 *
 * THE CARVE (Johnson/Yannakakis/Togelius 2010, research §2.1). A fine sub-grid over the
 * layout starts as solid rock. The layout's own rooms and corridors are stamped OPEN
 * and stay open; a band of seeded random noise around them is stamped open at
 * {@link SEED_OPEN_P}; then {@link SMOOTH_PASSES} passes of the classic 4/5 rule
 * (a cell goes solid when ≥5 of its 8 neighbours are solid; off-grid counts as solid)
 * smooth the noise into chambers and tunnels with organic edges.
 *
 * ⚠ THE CA IS CONSTRAINED, NOT TRUSTED. Unconstrained CA caves are famous for
 * disconnected pockets and sealed passages. Two invariants make that impossible here
 * rather than unlikely:
 *   1. every cell overlapping a layout room, or within a corridor half-width of a
 *      corridor centreline, is re-stamped OPEN after every pass — the mission's
 *      connectivity is a boundary condition on the automaton, not an outcome of it;
 *   2. every cell beyond {@link CARVE_MARGIN_M} outside the layout extent is re-stamped
 *      SOLID after every pass — the level cannot leak into the apron.
 * The render gate asserts both directly, plus a containment sweep that fires probes
 * radially out of every room and requires each to hit rock.
 *
 * MARCHING CUBES STAYS DEFERRED (the plan says so, and this is why): merged axis-
 * aligned prisms reduce EXACTLY to the one collider vocabulary — a box per rock
 * rectangle — so the picture and the collision are the same object. An isosurface
 * would need a second collision representation, which is the failure this project
 * has repeatedly avoided.
 *
 * DETERMINISM. Everything derives from the layout plus `rngFromString(site:epoch:hollow)`.
 * The CA is seeded from that stream in fixed cell order, so two dresses of one layout
 * produce byte-identical prism sets (pinned by the render gate).
 */
import * as THREE from "three";
import {
  CACHE_PAD_RADIUS,
  CACHE_WALL_PARAMS,
  rngFromString,
  type BoxColliderField,
  type CacheLayoutV1,
  type CachePropKind,
  type CacheRole,
} from "@hyperlight/shared";
import {
  addBoxInstances,
  addGroundMesh,
  addUnderplate,
  themeSink,
  addMarkers,
  addProps,
  addRoomFloors,
  boxRenderY,
  buildBoxField,
  buildGates,
  corridorMidpoint,
  makeValueNoise,
  roomFloorHeights,
  smooth01,
  WalkField,
  type CacheDressing,
  type CacheGate,
  type PropStyle,
  type ThemeOptions,
  type VisualCtx,
  type WallBox,
} from "./common";

// ── knobs (binding — the render gate reads several of these) ───────────────────

/** Half-width of a protected tunnel, metres. Narrower than `ruin`'s on purpose: the
 *  protected band is what the CA may never close, so a wide one eats the rock between
 *  neighbouring tunnels (18 m apart at tier 0) and the cave reads as one quarry instead
 *  of chambers linked by passages. At 4.0 an 8 m tunnel leaves 10 m of rock in an 18 m
 *  pitch — a BETTER ratio than the pre-scale-up 4.8 m tunnel in a 12 m pitch, which is
 *  why the widening did not have to be paid for here.
 *  ⚠ ONE SOURCE OF TRUTH with the canonical set — see `CORRIDOR_HALF` in ruin.ts. */
export const HOLLOW_CORRIDOR_HALF = CACHE_WALL_PARAMS.hollow.corridorHalf;
/** Working cell subdivisions per axis — the CA cell is `layout.cell / this` (3.0–3.3 m).
 *  Fine enough that the PROTECTED band hugs the tunnel: protection has to cover every
 *  cell whose square touches the tunnel, so a coarse cell inflates a 6 m tunnel into a
 *  13 m one and, at the working-cell pitch, the whole level merges into a single quarry.
 *  ⚠ THE SUB-GRID SCALES WITH `layout.cell`, so the CA's CELL COUNT is ~invariant under
 *  the 2026-08-02 scale-up (extent and `sub` both grew ~50%) — the carve got coarser in
 *  metres, not more expensive. */
export const CARVE_SUBDIV = 6;
/** Everything further outside the layout extent than this is forced SOLID. */
export const CARVE_MARGIN_M = 5;
/** Probability a noise-band cell seeds OPEN before smoothing. */
export const SEED_OPEN_P = 0.42;
/** Cells (Chebyshev) around the protected core the noise band covers. */
export const SEED_BAND_CELLS = 2;
/** Classic 4/5 smoothing passes. */
export const SMOOTH_PASSES = 4;
/** A solid cell within this many cells of open rock-faces gets a prism. */
export const ROCK_BAND_CELLS = 2;
/** Half-extent cap on a merged prism/lid, metres — see the ⚠⚠ on {@link greedyRects}.
 *  Comfortably under the collider field's 40 m feature-reach invariant. */
export const MAX_RECT_HALF_M = 18;
/** Minimum clear headroom in a tunnel / over a chamber, metres. */
export const HOLLOW_TUNNEL_HEADROOM = 3.6;
export const HOLLOW_CHAMBER_HEADROOM = 5.2;
/** Ceiling variance above those minima, metres (the stalactite-ish read). */
const TUNNEL_VARY = 1.0;
const CHAMBER_VARY = 1.6;
/** Ceiling slab thickness, metres. */
const CEIL_T = 0.6;
/** Extra lift on a lid, metres — cell centres are sampled, the rect reaches half a
 *  cell further, and the floor keeps undulating out there. */
const CEIL_SAFETY = 0.4;
/** How far a rock prism sinks below the floor it stands on, metres. */
const ROCK_SINK = 1.6;
/** Rock/ceiling top above the highest floor, metres. */
const ROCK_TOP_H = 10.5;
/** Floor undulation amplitude / wavelength, metres. */
export const FLOOR_UNDULATE = 0.8;
const UNDULATE_SCALE = 26;
/** Undulation damping on walkable ground (rooms and tunnels stay flat-ish). */
const WALK_DAMP = 0.35;
const DAMP_RUN = 5;
/** Height ladder: `FLOOR_LADDER.hollow` in shared/src/cache/layout.ts. */
const SINK = 0.4;
/** Gate throat: how far past the tunnel half-width the carve is pinched back to
 *  rock around a gate, metres (see the throat pass in `buildCarve`). */
const THROAT_PAD = 1.0;
/** Dynamic PointLights allowed in one hollow. */
const MAX_POINT_LIGHTS = 8;

/** Floor tint per room role — damp rock, cool. */
const ROLE_COLOR: Record<CacheRole, number> = {
  entrance: 0x2b6b86,
  transit: 0x3d4450,
  arena: 0x5a5344,
  gate: 0x8a6f2c,
  key: 0x3f8a63,
  treasure: 0xa88c3a,
  boss: 0x82323c,
  shortcut: 0x4b4478,
  vista: 0x357a72,
};

const PROP_STYLE: Record<CachePropKind, PropStyle> = {
  loot: { color: 0xd8bb52, roughness: 0.35, metalness: 0.55 },
  // Bioluminescence, not torchlight — the cool half of the theme split.
  light: { color: 0xa9f6ff, emissive: 0x36d7e8, emissiveIntensity: 2.2 },
  cover: { color: 0x4c4a46, roughness: 1 },
  hazard: { color: 0xb4432f, roughness: 0.85 },
  marker: { color: 0xbfd6df, roughness: 0.75 },
};

// ── public shape ──────────────────────────────────────────────────────────────

export interface HollowDressing extends CacheDressing {
  /** Ceiling shell boxes (merged over the carved region, variable height). */
  readonly ceilings: readonly WallBox[];
  /** The carve, for the render gate's constrained-carve assertions. */
  readonly carve: CarveGrid;
}

/** The cellular-automata result, in its own integer frame. */
export interface CarveGrid {
  /** Cells per axis. */
  n: number;
  /** Metres per cell. */
  sub: number;
  /** 1 = open (walkable void), 0 = solid rock. Row-major, `j * n + i`. */
  open: Uint8Array;
  /** 1 = protected open (a layout room/corridor cell) — re-stamped every pass. */
  protect: Uint8Array;
  /** 1 = forced solid (beyond the margin, or a gate throat). */
  forced: Uint8Array;
  /** Pad-local metres of cell index `i` on either axis. */
  pos(i: number): number;
  /** Cell index containing pad-local `x` (clamped). */
  cell(x: number): number;
}

// ── build ─────────────────────────────────────────────────────────────────────

/**
 * Dress a layout as a cave.
 *
 * Returns the heightfield the pad host displaces its sphere with, the solid boxes the
 * character collides against (rock prisms, ceiling shell, closed gates), and — unless
 * `visuals: false` — the render-space scene content. Pure in the layout + its seed.
 */
export function dressHollow(layout: CacheLayoutV1, opts: ThemeOptions = {}): HollowDressing {
  const R = opts.radius ?? CACHE_PAD_RADIUS;
  const rng = rngFromString(`${layout.site}:${layout.epoch}:hollow`);

  // ── 1. floors ───────────────────────────────────────────────────────────────
  const floorH = roomFloorHeights(layout, rng, opts);
  // Live mode sinks every solid past the host planet's sampler relief (see themeSink).
  const sink = themeSink(SINK, opts);
  const rockSink = themeSink(ROCK_SINK, opts);
  let highest = 0;
  for (let i = 0; i < floorH.length; i++) highest = Math.max(highest, floorH[i]);
  const rockTop = highest + ROCK_TOP_H;

  const walk = new WalkField(layout, floorH, HOLLOW_CORRIDOR_HALF, 48);
  const undulate = makeValueNoise(rng, FLOOR_UNDULATE, UNDULATE_SCALE);

  /** Walkable floor: the layout's ladder plus gentle undulation, damped on the level. */
  const floorAt = (x: number, z: number): number => {
    const s = walk.sample(x, z);
    const base = s.d === Infinity ? 0 : s.h;
    const damp = WALK_DAMP + (1 - WALK_DAMP) * smooth01(Math.max(0, s.d) / DAMP_RUN);
    return base + undulate(x, z) * damp;
  };

  // ── 2. the carve ────────────────────────────────────────────────────────────
  const carve = buildCarve(layout, walk, rng);
  const { n: N, sub, open } = carve;

  /** Chebyshev cell distance from every cell to the nearest OPEN cell (0 when open). */
  const distOpen = distanceToOpen(carve);

  /**
   * Rock MASS at (x, z) in [0, 1] — 0 on the cave floor and in the prism band, 1 deep
   * inside solid rock. Bilinear over the cell field so the height blend is continuous
   * (a per-cell step would put a visible cliff at every rock face).
   */
  const massAt = (x: number, z: number): number => {
    const fi = (x - carve.pos(0)) / sub, fj = (z - carve.pos(0)) / sub;
    const i0 = Math.floor(fi), j0 = Math.floor(fj);
    if (i0 < 0 || j0 < 0 || i0 + 1 >= N || j0 + 1 >= N) return 1;
    const tu = fi - i0, tv = fj - j0;
    const m = (i: number, j: number): number => smooth01((distOpen[j * N + i] - ROCK_BAND_CELLS) / 2);
    const a = m(i0, j0) + (m(i0 + 1, j0) - m(i0, j0)) * tu;
    const b = m(i0, j0 + 1) + (m(i0 + 1, j0 + 1) - m(i0, j0 + 1)) * tu;
    return a + (b - a) * tv;
  };

  const heightAt = (x: number, z: number): number => {
    const f = floorAt(x, z);
    const m = massAt(x, z);
    return m <= 0 ? f : f + (rockTop - f) * m;
  };

  const sphereY = (x: number, z: number): number => {
    const rh = R + heightAt(x, z);
    return Math.sqrt(Math.max(0, rh * rh - x * x - z * z)) - R;
  };

  // ── 3. rock prisms: merged solid cells adjacent to the void ─────────────────
  const rockMask = new Uint8Array(N * N);
  for (let k = 0; k < rockMask.length; k++) {
    rockMask[k] = open[k] === 0 && distOpen[k] <= ROCK_BAND_CELLS ? 1 : 0;
  }
  const maxCells = Math.max(1, Math.floor((2 * MAX_RECT_HALF_M) / sub));
  const walls: WallBox[] = [];
  for (const [i0, j0, w, h] of greedyRects(rockMask, N, maxCells)) {
    const x0 = carve.pos(i0) - sub / 2, x1 = carve.pos(i0 + w - 1) + sub / 2;
    const z0 = carve.pos(j0) - sub / 2, z1 = carve.pos(j0 + h - 1) + sub / 2;
    // Sample EVERY cell, not the corners: a merged rect can span a ramp, and a prism
    // based on the corner floors floats above the lowest ground it covers.
    let lowest = Infinity;
    for (let j = j0; j < j0 + h; j++) {
      for (let i = i0; i < i0 + w; i++) lowest = Math.min(lowest, floorAt(carve.pos(i), carve.pos(j)));
    }
    const baseH = lowest - rockSink;
    walls.push({
      cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, along: "x",
      halfLen: (x1 - x0) / 2, halfT: (z1 - z0) / 2,
      baseH, height: rockTop - baseH,
    });
  }

  // ── 4. ceiling shell: merged open cells, higher over chambers ───────────────
  const ceilings: WallBox[] = [];
  const openMask = new Uint8Array(N * N);
  for (let k = 0; k < openMask.length; k++) openMask[k] = open[k];
  for (const [i0, j0, w, h] of greedyRects(openMask, N, maxCells)) {
    const x0 = carve.pos(i0) - sub / 2, x1 = carve.pos(i0 + w - 1) + sub / 2;
    const z0 = carve.pos(j0) - sub / 2, z1 = carve.pos(j0 + h - 1) + sub / 2;
    // Headroom is measured off the HIGHEST floor the rect spans, cell by cell: one slab
    // over a ramping tunnel must clear the TOP of the ramp, not its middle (sampling
    // corners only put ceilings under the floor — the render gate caught it). A rect
    // that touches ANY room is a chamber and gets the taller lid, however its centre
    // happens to land after the merge.
    let top = -Infinity, chamber = false;
    for (let j = j0; j < j0 + h; j++) {
      for (let i = i0; i < i0 + w; i++) {
        const px = carve.pos(i), pz = carve.pos(j);
        const f = floorAt(px, pz);
        if (f > top) top = f;
        if (!chamber && inRoom(layout, px, pz)) chamber = true;
      }
    }
    const clear = chamber
      ? HOLLOW_CHAMBER_HEADROOM + rng() * CHAMBER_VARY
      : HOLLOW_TUNNEL_HEADROOM + rng() * TUNNEL_VARY;
    ceilings.push({
      cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, along: "x",
      halfLen: (x1 - x0) / 2, halfT: (z1 - z0) / 2,
      baseH: top + clear + CEIL_SAFETY, height: CEIL_T,
    });
  }

  // ── 5. gates ────────────────────────────────────────────────────────────────
  // The carve pinches to the tunnel width at every gate (the throat pass), so a
  // barrier one cell wider than the tunnel seals it with no CA bulge to walk around.
  const gates: CacheGate[] = buildGates(layout, walk, {
    halfLen: HOLLOW_CORRIDOR_HALF + sub,
    halfT: 0.3,
    height: HOLLOW_TUNNEL_HEADROOM + 0.4,
    sink,
  });

  const buildField = (): BoxColliderField => buildBoxField(R, [walls, ceilings], gates);

  // ── 6. visuals ──────────────────────────────────────────────────────────────
  const group = new THREE.Group();
  group.name = "cacheHollow";
  const disposables: { dispose(): void }[] = [];
  const gateMeshes = new Map<number, THREE.Object3D>();
  if (opts.visuals !== false) {
    buildVisuals(layout, group, gateMeshes, {
      sphereY, heightAt,
      keep: <T extends { dispose(): void }>(o: T): T => { disposables.push(o); return o; },
    }, walls, ceilings, gates, opts, R);
  }

  const dressing: HollowDressing = {
    layout,
    radius: R,
    heightAt,
    sphereY,
    colliders: buildField(),
    group,
    walls,
    ceilings,
    gates,
    carve,
    floorH: Array.from(floorH),
    setGateOpen(edgeIndex: number, open2: boolean): BoxColliderField {
      const g = gates.find((x) => x.edgeIndex === edgeIndex);
      if (g === undefined) throw new Error(`setGateOpen: edge ${edgeIndex} is not a gate`);
      g.open = open2;
      const mesh = gateMeshes.get(edgeIndex);
      if (mesh) mesh.visible = !open2;
      dressing.colliders = buildField();
      return dressing.colliders;
    },
    dispose(): void {
      for (const d of disposables) d.dispose();
      disposables.length = 0;
      group.clear();
    },
  };
  return dressing;
}

// ── the automaton ─────────────────────────────────────────────────────────────

function inRoom(layout: CacheLayoutV1, x: number, z: number): boolean {
  for (const r of layout.rooms) {
    if (Math.abs(x - r.x) <= r.w / 2 && Math.abs(z - r.z) <= r.d / 2) return true;
  }
  return false;
}

/**
 * Seed and run the constrained automaton.
 *
 * Draw order is fixed (row-major over the seed band, then pass by pass), so the whole
 * carve is a pure function of the layout and the `hollow` sub-seed.
 */
function buildCarve(layout: CacheLayoutV1, walk: WalkField, rng: () => number): CarveGrid {
  const sub = layout.cell / CARVE_SUBDIV;
  const half = Math.ceil((layout.extent + CARVE_MARGIN_M + 3 * sub) / sub);
  const n = 2 * half + 1;
  const pos = (i: number): number => (i - half) * sub;
  const cell = (x: number): number => Math.min(n - 1, Math.max(0, Math.round(x / sub) + half));

  const protect = new Uint8Array(n * n);
  const forced = new Uint8Array(n * n);
  const guard = layout.extent + CARVE_MARGIN_M;

  // Protected core: a cell whose SQUARE overlaps a room (not merely whose centre sits
  // inside it) and a cell within a tunnel half-width + half a cell of a corridor. Using
  // the square, not the centre, is what keeps rock from jutting into a chamber and
  // blocking the walk volume the render gate sweeps.
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const x = pos(i), z = pos(j);
      const k = j * n + i;
      if (Math.max(Math.abs(x), Math.abs(z)) > guard) { forced[k] = 1; continue; }
      let ok = false;
      for (const r of layout.rooms) {
        if (Math.abs(x - r.x) <= r.w / 2 + sub / 2 && Math.abs(z - r.z) <= r.d / 2 + sub / 2) { ok = true; break; }
      }
      if (!ok) {
        for (let ci = 0; ci < walk.corrs.length; ci++) {
          if (walk.corrDist(ci, x, z) <= walk.corridorHalf + sub / 2) { ok = true; break; }
        }
      }
      if (ok) protect[k] = 1;
    }
  }

  // Gate throats: pinch the carve back to the tunnel around every gate so the barrier
  // cannot be walked around through a CA bulge.
  layout.edges.forEach((e, ei) => {
    if (e.kind !== "gate") return;
    const m = corridorMidpoint(walk.corrs[ei]);
    const rCells = Math.ceil((walk.corridorHalf + 3 * sub) / sub);
    const i0 = cell(m.cx), j0 = cell(m.cz);
    for (let j = j0 - rCells; j <= j0 + rCells; j++) {
      for (let i = i0 - rCells; i <= i0 + rCells; i++) {
        if (i < 0 || j < 0 || i >= n || j >= n) continue;
        const x = pos(i), z = pos(j);
        if (Math.hypot(x - m.cx, z - m.cz) > walk.corridorHalf + 3 * sub) continue;
        if (walk.corrDist(ei, x, z) <= walk.corridorHalf + THROAT_PAD) continue;
        // ⚠ NEVER pinch another feature shut. A throat radius reaches ~13 m, which can
        // cover a neighbouring chamber or a crossing tunnel; forcing those solid walled
        // rooms off and sealed legal routes (caught by the render gate on first run).
        // A crossing tunnel therefore still bypasses a gate geometrically — the same
        // TODO(P5) ruin.ts carries, closed when the server owns progression.
        if (protect[j * n + i]) continue;
        forced[j * n + i] = 1;
      }
    }
  });

  // Seed: protected core + one dilation + random fill in a band around it.
  const open = new Uint8Array(n * n);
  const dist = bfsDistance(protect, n);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const k = j * n + i;
      if (forced[k]) continue;
      if (protect[k]) { open[k] = 1; continue; }
      // NO unconditional dilation. One free ring around a 9.5 m protected tunnel makes
      // it 16 m wide, and at a 12 m cell pitch the neighbouring tunnels then merge into
      // a single quarry — the cave loses its chambers-and-tunnels read entirely. The
      // band alone (smoothed) is what grows the organic bulges.
      if (dist[k] <= SEED_BAND_CELLS && rng() < SEED_OPEN_P) open[k] = 1;
    }
  }

  // Smooth: the classic 4/5 rule, with both boundary conditions re-applied every pass.
  const next = new Uint8Array(n * n);
  for (let p = 0; p < SMOOTH_PASSES; p++) {
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        let solid = 0;
        for (let dj = -1; dj <= 1; dj++) {
          for (let di = -1; di <= 1; di++) {
            if (di === 0 && dj === 0) continue;
            const ii = i + di, jj = j + dj;
            if (ii < 0 || jj < 0 || ii >= n || jj >= n) { solid++; continue; }  // off-grid is rock
            if (open[jj * n + ii] === 0) solid++;
          }
        }
        const k = j * n + i;
        next[k] = solid >= 5 ? 0 : 1;
        if (protect[k]) next[k] = 1;
        if (forced[k]) next[k] = 0;
      }
    }
    open.set(next);
  }

  return { n, sub, open, protect, forced, pos, cell };
}

/** Multi-source Chebyshev BFS distance from the set cells of `src`. */
function bfsDistance(src: Uint8Array, n: number): Int32Array {
  const dist = new Int32Array(n * n).fill(1 << 24);
  const q = new Int32Array(n * n);
  let head = 0, tail = 0;
  for (let k = 0; k < src.length; k++) if (src[k]) { dist[k] = 0; q[tail++] = k; }
  while (head < tail) {
    const k = q[head++];
    const i = k % n, j = (k / n) | 0;
    for (let dj = -1; dj <= 1; dj++) {
      for (let di = -1; di <= 1; di++) {
        if (di === 0 && dj === 0) continue;
        const ii = i + di, jj = j + dj;
        if (ii < 0 || jj < 0 || ii >= n || jj >= n) continue;
        const kk = jj * n + ii;
        if (dist[kk] > dist[k] + 1) { dist[kk] = dist[k] + 1; q[tail++] = kk; }
      }
    }
  }
  return dist;
}

/** Chebyshev cell distance to the nearest OPEN cell (0 for open cells themselves). */
function distanceToOpen(c: CarveGrid): Int32Array {
  return bfsDistance(c.open, c.n);
}

/**
 * Greedy maximal-rectangle cover of a bitmask — the merge that keeps one cave from
 * becoming ten thousand unit boxes. Row-major scan, widest-then-tallest, so the cover
 * is deterministic and every set cell lands in exactly one rectangle.
 *
 * ⚠⚠ `maxCells` IS A COLLISION CORRECTNESS BOUND, NOT A PERF KNOB. `BoxColliderField`
 * buckets by cube-face direction and sizes its grid so that a feature whose bounding
 * radius is ≤ `COLLIDER_MAX_M` (40 m) never reaches past the buckets it is smeared
 * into. An unbounded merge happily produced a 3.5 × 189 m ceiling strip, whose 95 m
 * reach broke that invariant — and the symptom was a room whose lid simply did not
 * answer queries from most of the room. The render gate caught it as "2 of 546 rooms
 * unroofed"; on a player it would have been a ceiling you sometimes fall through.
 */
export function greedyRects(mask: Uint8Array, n: number, maxCells = 1 << 24): [number, number, number, number][] {
  const used = new Uint8Array(n * n);
  const out: [number, number, number, number][] = [];
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const k = j * n + i;
      if (!mask[k] || used[k]) continue;
      let w = 0;
      while (i + w < n && w < maxCells && mask[j * n + i + w] && !used[j * n + i + w]) w++;
      let h = 1;
      grow: while (j + h < n && h < maxCells) {
        for (let a = 0; a < w; a++) {
          const kk = (j + h) * n + i + a;
          if (!mask[kk] || used[kk]) break grow;
        }
        h++;
      }
      for (let b = 0; b < h; b++) for (let a = 0; a < w; a++) used[(j + b) * n + i + a] = 1;
      out.push([i, j, w, h]);
    }
  }
  return out;
}

// ── visuals ───────────────────────────────────────────────────────────────────

function buildVisuals(
  layout: CacheLayoutV1,
  group: THREE.Group,
  gateMeshes: Map<number, THREE.Object3D>,
  ctx: VisualCtx,
  walls: readonly WallBox[],
  ceilings: readonly WallBox[],
  gates: readonly CacheGate[],
  opts: ThemeOptions,
  radius: number,
): void {
  // Live mode: the host Planet already draws a ground — a second one interpenetrates
  // it — so the heightfield ground is replaced by a sunk backdrop plate.
  if (opts.flat === true) addUnderplate(group, ctx, layout, 0x3a3630, radius);
  else addGroundMesh(group, ctx, layout, 0x3a3630);
  addRoomFloors(group, ctx, layout, (role) => ROLE_COLOR[role]);

  addBoxInstances(group, ctx, walls, "cacheWalls", ctx.keep(
    new THREE.MeshStandardMaterial({ color: 0x565049, roughness: 1, flatShading: true }),
  ));
  const ceilGroup = new THREE.Group();
  ceilGroup.name = "cacheCeilings";
  addBoxInstances(ceilGroup, ctx, ceilings, "cacheCeilingSlabs", ctx.keep(
    new THREE.MeshStandardMaterial({ color: 0x413b36, roughness: 1, flatShading: true }),
  ));
  group.add(ceilGroup);

  {
    const geo = ctx.keep(new THREE.BoxGeometry(1, 1, 1));
    const mat = ctx.keep(new THREE.MeshStandardMaterial({
      color: 0x2f6f76, emissive: 0x1c5a63, emissiveIntensity: 0.6, roughness: 0.6, metalness: 0.3,
    }));
    for (const g of gates) {
      const b = g.box;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.scale.set(
        b.along === "x" ? b.halfLen * 2 : b.halfT * 2,
        b.height,
        b.along === "x" ? b.halfT * 2 : b.halfLen * 2,
      );
      mesh.position.set(b.cx, boxRenderY(ctx, b), b.cz);
      mesh.visible = !g.open;
      mesh.name = `cacheGate${g.edgeIndex}`;
      group.add(mesh);
      gateMeshes.set(g.edgeIndex, mesh);
    }
  }

  addProps(group, ctx, layout, PROP_STYLE, {
    color: 0x6fe6f2, intensity: 22, distance: 24, max: MAX_POINT_LIGHTS,
  });
  addMarkers(group, ctx, layout,
    { color: 0x5fd0f0, emissive: 0x1d6b86 },
    { color: 0x8e2f2f, emissive: 0x3a0c0c });
}

/** Role tint, exported for the lab's mission-graph overlay. */
export function hollowRoleColor(role: CacheRole): number {
  return ROLE_COLOR[role];
}
